import { useEffect, useRef, useState } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { statusLabel, statusBg, statusText, formatSqYd } from "@/lib/statusUtils";
import { toast } from "sonner";
import { Upload, Pencil, Trash2, Plus, Image as ImageIcon, History } from "lucide-react";

const emptyPlot = { plot_no: "", total_sqyd: "", vacant_sqyd: "", vacant_portion: "", status: "fully_vacant", remarks: "", mortgage_flag: false, mortgage_note: "" };
const emptyLp = { phase_name: "", lp_no: "", layout_acres: "", remarks: "" };
const emptyStatusChange = { status: "fully_vacant", reason: "" };

export default function AdminPlots() {
  const [tab, setTab] = useState("lps");
  const [lps, setLps] = useState([]);
  const [currentLpId, setCurrentLpId] = useState(null);
  const [plots, setPlots] = useState([]);
  const [plotDialog, setPlotDialog] = useState(null); // {mode:'create'|'edit', plot}
  const [plotForm, setPlotForm] = useState(emptyPlot);
  const [lpEdit, setLpEdit] = useState(null);
  const [lpForm, setLpForm] = useState(emptyLp);
  const [statusChange, setStatusChange] = useState(null); // {plot}
  const [statusForm, setStatusForm] = useState(emptyStatusChange);
  const fileInputRef = useRef(null);
  const [uploadingFor, setUploadingFor] = useState(null);

  const loadLps = async () => {
    try {
      const { data } = await api.get("/lps");
      setLps(data);
      if (!currentLpId && data.length) setCurrentLpId(data[0].id);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadLps(); }, []);

  const loadPlots = async () => {
    if (!currentLpId) return;
    try {
      const { data } = await api.get("/plots", { params: { lp_id: currentLpId, limit: 2000 } });
      setPlots(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { if (tab === "plots") loadPlots(); /* eslint-disable-next-line */ }, [currentLpId, tab]);

  // ---- LP actions ----
  const openLpEdit = (lp) => {
    setLpEdit(lp);
    setLpForm({ phase_name: lp.phase_name || "", lp_no: lp.lp_no || "", layout_acres: lp.layout_acres || "", remarks: lp.remarks || "" });
  };
  const saveLpEdit = async () => {
    try {
      const payload = { ...lpForm, layout_acres: lpForm.layout_acres ? parseFloat(lpForm.layout_acres) : null };
      await api.patch(`/lps/${lpEdit.id}`, payload);
      toast.success("LP updated"); setLpEdit(null); loadLps();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const triggerUpload = (lpId) => {
    setUploadingFor(lpId);
    fileInputRef.current?.click();
  };
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !uploadingFor) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const token = localStorage.getItem("anu_token");
      const r = await fetch(`${API_BASE}/lps/${uploadingFor}/layout-image`, {
        method: "POST",
        body: fd,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Upload failed");
      toast.success("Layout image uploaded");
      loadLps();
    } catch (e2) { toast.error(e2.message || "Upload failed"); }
    finally { setUploadingFor(null); }
  };

  // ---- Plot actions ----
  const openPlotCreate = () => {
    if (!currentLpId) { toast.error("Select an LP first"); return; }
    setPlotDialog({ mode: "create" });
    setPlotForm(emptyPlot);
  };
  const openPlotEdit = (p) => {
    setPlotDialog({ mode: "edit", plot: p });
    setPlotForm({
      plot_no: p.plot_no ?? "", total_sqyd: p.total_sqyd ?? "", vacant_sqyd: p.vacant_sqyd ?? "",
      vacant_portion: p.vacant_portion || "", status: p.status || "fully_vacant",
      remarks: p.remarks || "", mortgage_flag: !!p.mortgage_flag, mortgage_note: p.mortgage_note || "",
    });
  };
  const savePlot = async () => {
    const payload = {
      ...plotForm,
      plot_no: plotForm.plot_no ? parseInt(plotForm.plot_no, 10) : null,
      total_sqyd: plotForm.total_sqyd ? parseFloat(plotForm.total_sqyd) : null,
      vacant_sqyd: plotForm.vacant_sqyd ? parseFloat(plotForm.vacant_sqyd) : null,
    };
    try {
      if (plotDialog.mode === "create") {
        await api.post("/plots", { lp_id: currentLpId, ...payload });
        toast.success("Plot created");
      } else {
        await api.patch(`/plots/${plotDialog.plot.id}`, payload);
        toast.success("Plot updated");
      }
      setPlotDialog(null); loadPlots(); loadLps();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const delPlot = async (p) => {
    if (!window.confirm(`Delete plot #${p.plot_no}? This cannot be undone.`)) return;
    try {
      await api.delete(`/plots/${p.id}`);
      toast.success("Plot deleted"); loadPlots(); loadLps();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openStatusChange = (p) => {
    setStatusChange({ plot: p });
    setStatusForm({ status: p.status || "fully_vacant", reason: "" });
  };
  const submitStatusChange = async () => {
    if (!statusForm.reason.trim()) { toast.error("Reason is required"); return; }
    if (statusForm.status === statusChange.plot.status) { toast.error("New status must differ from current"); return; }
    try {
      const { data } = await api.post(`/plots/${statusChange.plot.id}/status`, statusForm);
      toast.success(`Status changed: ${data.from} → ${data.to}`);
      setStatusChange(null);
      loadPlots(); loadLps();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadExport = () => {
    const params = currentLpId ? `?lp_id=${currentLpId}` : "";
    const token = localStorage.getItem("anu_token");
    fetch(`${API_BASE}/export/plots.xlsx${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = "anuttara-plots.xlsx"; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Export failed"));
  };

  return (
    <AppShell>
      <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleFile} data-testid="hidden-file-input" />
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-12 py-6 md:py-8">
        <div className="mb-6">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#C06C47] font-semibold mb-2">Super Admin</div>
          <h1 className="font-heading text-2xl md:text-4xl">Manage LPs & Plots</h1>
          <p className="text-sm text-[#5C6653] mt-2">Add, edit, delete plots. Upload official layout images per LP.</p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-sm flex-wrap h-auto">
            <TabsTrigger value="lps" data-testid="tab-lps">LP Layouts</TabsTrigger>
            <TabsTrigger value="plots" data-testid="tab-plots">Plots</TabsTrigger>
          </TabsList>
        </Tabs>

        {tab === "lps" && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="lps-grid">
            {lps.map((lp) => (
              <div key={lp.id} className="border border-[#E5E2DC] bg-white p-5" data-testid={`lp-card-manage-${lp.lp_no}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#C06C47] font-semibold">{lp.phase_name}</div>
                    <div className="font-heading text-xl mt-1">{lp.lp_no}</div>
                    <div className="text-xs text-[#5C6653] mt-1">{lp.layout_acres ? `${lp.layout_acres} acres` : "—"} · {lp.stats?.total || 0} plots</div>
                  </div>
                  <button onClick={() => openLpEdit(lp)} data-testid={`edit-lp-${lp.lp_no}`} className="h-8 w-8 flex items-center justify-center border border-[#E5E2DC] hover:bg-[#F0EFEA] rounded-sm">
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="mt-4">
                  {lp.layout_image_url ? (
                    <div className="border border-[#E5E2DC] overflow-hidden">
                      <img src={lp.layout_image_url} alt={lp.lp_no} className="w-full max-h-40 object-cover" />
                    </div>
                  ) : (
                    <div className="border border-dashed border-[#D6D3CA] text-center py-6 text-xs text-[#5C6653]">
                      <ImageIcon className="h-5 w-5 mx-auto mb-2 opacity-60" />
                      No layout image uploaded
                    </div>
                  )}
                </div>
                <Button
                  onClick={() => triggerUpload(lp.id)}
                  data-testid={`upload-layout-${lp.lp_no}`}
                  variant="outline"
                  className="mt-3 w-full rounded-sm"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {lp.layout_image_url ? "Replace layout image" : "Upload layout image"}
                </Button>
              </div>
            ))}
          </div>
        )}

        {tab === "plots" && (
          <div className="mt-4">
            <div className="border border-[#E5E2DC] bg-white p-4 flex flex-wrap gap-3 mb-4 items-center">
              <Select value={currentLpId || ""} onValueChange={setCurrentLpId}>
                <SelectTrigger className="w-72 rounded-sm" data-testid="plots-lp-select">
                  <SelectValue placeholder="Select LP" />
                </SelectTrigger>
                <SelectContent>
                  {lps.map((lp) => <SelectItem key={lp.id} value={lp.id}>{lp.phase_name} · {lp.lp_no}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={openPlotCreate} data-testid="create-plot-btn" className="rounded-sm bg-[#234B36] hover:bg-[#1A3A29] text-white">
                <Plus className="h-4 w-4 mr-2" /> Add plot
              </Button>
              <Button onClick={downloadExport} variant="outline" className="rounded-sm ml-auto" data-testid="export-plots-btn">
                Export Excel
              </Button>
            </div>

            <div className="border border-[#E5E2DC] bg-white overflow-x-auto">
              <table className="w-full text-sm min-w-[700px]" data-testid="admin-plots-table">
                <thead className="bg-[#F0EFEA] text-left">
                  <tr className="border-b border-[#E5E2DC]">
                    <Th>Plot #</Th>
                    <Th>Total</Th>
                    <Th>Vacant</Th>
                    <Th>Status</Th>
                    <Th>Mortgage</Th>
                    <Th>Remarks</Th>
                    <Th>Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {plots.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-12 text-[#5C6653]">No plots. Select an LP or add a new plot.</td></tr>
                  ) : plots.map((p) => (
                    <tr key={p.id} className="border-b border-[#E5E2DC]" data-testid={`plot-row-${p.plot_no}`}>
                      <Td><span className="font-mono-anu font-medium">#{p.plot_no}</span></Td>
                      <Td>{formatSqYd(p.total_sqyd)}</Td>
                      <Td>{p.vacant_sqyd != null ? formatSqYd(p.vacant_sqyd) : "—"}</Td>
                      <Td>
                        <span className="text-xs px-2 py-0.5 border font-medium" style={{ background: `${statusBg(p.status)}30`, borderColor: statusBg(p.status), color: statusText(p.status) === "#FFFFFF" ? statusBg(p.status) : statusText(p.status) }}>
                          {statusLabel(p.status)}
                        </span>
                      </Td>
                      <Td>{p.mortgage_flag ? <span className="text-[#C06C47] text-xs">Yes</span> : <span className="text-xs text-[#5C6653]">—</span>}</Td>
                      <Td><span className="text-xs text-[#5C6653]">{p.remarks || "—"}</span></Td>
                      <Td>
                        <div className="flex gap-1.5">
                          <button onClick={() => openPlotEdit(p)} data-testid={`edit-plot-${p.plot_no}`} className="h-8 w-8 flex items-center justify-center border border-[#E5E2DC] hover:bg-[#F0EFEA] rounded-sm" title="Edit plot"><Pencil className="h-3.5 w-3.5" /></button>
                          <button onClick={() => openStatusChange(p)} data-testid={`status-change-plot-${p.plot_no}`} className="h-8 w-8 flex items-center justify-center border border-[#C06C47] text-[#C06C47] hover:bg-[#C06C47]/10 rounded-sm" title="Change / rollback status"><History className="h-3.5 w-3.5" /></button>
                          <button onClick={() => delPlot(p)} data-testid={`delete-plot-${p.plot_no}`} className="h-8 w-8 flex items-center justify-center border border-red-300 text-red-700 hover:bg-red-50 rounded-sm" title="Delete plot"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* LP edit */}
      <Dialog open={!!lpEdit} onOpenChange={(o) => !o && setLpEdit(null)}>
        <DialogContent className="max-w-md rounded-sm">
          {lpEdit && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Edit LP · {lpEdit.lp_no}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3" data-testid="edit-lp-form">
                <F label="Phase Name"><Input value={lpForm.phase_name} onChange={(e) => setLpForm({ ...lpForm, phase_name: e.target.value })} className="rounded-sm" data-testid="edit-lp-phase" /></F>
                <F label="LP Number"><Input value={lpForm.lp_no} onChange={(e) => setLpForm({ ...lpForm, lp_no: e.target.value })} className="rounded-sm" data-testid="edit-lp-no" /></F>
                <F label="Layout Acres"><Input type="number" value={lpForm.layout_acres} onChange={(e) => setLpForm({ ...lpForm, layout_acres: e.target.value })} className="rounded-sm" data-testid="edit-lp-acres" /></F>
                <F label="Remarks"><Textarea value={lpForm.remarks} onChange={(e) => setLpForm({ ...lpForm, remarks: e.target.value })} rows={2} className="rounded-sm" data-testid="edit-lp-remarks" /></F>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setLpEdit(null)} className="rounded-sm">Cancel</Button>
                <Button onClick={saveLpEdit} data-testid="save-lp" className="rounded-sm bg-[#234B36] hover:bg-[#1A3A29] text-white">Save</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Plot create/edit */}
      <Dialog open={!!plotDialog} onOpenChange={(o) => !o && setPlotDialog(null)}>
        <DialogContent className="max-w-md rounded-sm">
          {plotDialog && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">
                  {plotDialog.mode === "create" ? "Add plot" : `Edit plot #${plotDialog.plot.plot_no}`}
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3" data-testid="plot-form">
                <F label="Plot #"><Input type="number" value={plotForm.plot_no} onChange={(e) => setPlotForm({ ...plotForm, plot_no: e.target.value })} className="rounded-sm" data-testid="plot-form-no" /></F>
                <F label="Total Sq.Yd"><Input type="number" value={plotForm.total_sqyd} onChange={(e) => setPlotForm({ ...plotForm, total_sqyd: e.target.value })} className="rounded-sm" data-testid="plot-form-total" /></F>
                <F label="Vacant Sq.Yd"><Input type="number" value={plotForm.vacant_sqyd} onChange={(e) => setPlotForm({ ...plotForm, vacant_sqyd: e.target.value })} className="rounded-sm" data-testid="plot-form-vacant" /></F>
                <F label="Vacant Portion"><Input value={plotForm.vacant_portion} onChange={(e) => setPlotForm({ ...plotForm, vacant_portion: e.target.value })} className="rounded-sm" data-testid="plot-form-portion" /></F>
                <F label="Status">
                  <Select value={plotForm.status} onValueChange={(v) => setPlotForm({ ...plotForm, status: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="plot-form-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fully_vacant">Available</SelectItem>
                      <SelectItem value="partially_vacant">Partial</SelectItem>
                      <SelectItem value="booked">Booked</SelectItem>
                      <SelectItem value="sold">Sold</SelectItem>
                      <SelectItem value="unknown">Unknown</SelectItem>
                    </SelectContent>
                  </Select>
                </F>
                <F label="Mortgage?">
                  <Select value={plotForm.mortgage_flag ? "yes" : "no"} onValueChange={(v) => setPlotForm({ ...plotForm, mortgage_flag: v === "yes" })}>
                    <SelectTrigger className="rounded-sm" data-testid="plot-form-mortgage"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="yes">Yes</SelectItem>
                    </SelectContent>
                  </Select>
                </F>
                <div className="col-span-2">
                  <F label="Remarks"><Textarea rows={2} value={plotForm.remarks} onChange={(e) => setPlotForm({ ...plotForm, remarks: e.target.value })} className="rounded-sm" data-testid="plot-form-remarks" /></F>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setPlotDialog(null)} className="rounded-sm">Cancel</Button>
                <Button onClick={savePlot} data-testid="save-plot" className="rounded-sm bg-[#234B36] hover:bg-[#1A3A29] text-white">
                  {plotDialog.mode === "create" ? "Create" : "Save"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Status change / rollback (super_admin) */}
      <Dialog open={!!statusChange} onOpenChange={(o) => !o && setStatusChange(null)}>
        <DialogContent className="max-w-md rounded-sm w-[calc(100vw-1.5rem)] sm:w-full">
          {statusChange && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Change plot status</DialogTitle>
              </DialogHeader>
              <div className="space-y-3" data-testid="status-change-form">
                <div className="text-xs text-[#5C6653]">
                  Plot <b>#{statusChange.plot.plot_no}</b> · {statusChange.plot.lp_no} — current status <b>{statusLabel(statusChange.plot.status)}</b>.
                </div>
                <F label="New status">
                  <Select value={statusForm.status} onValueChange={(v) => setStatusForm({ ...statusForm, status: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="status-change-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fully_vacant">Available (rollback)</SelectItem>
                      <SelectItem value="partially_vacant">Partial</SelectItem>
                      <SelectItem value="booked">Booked / Reserved</SelectItem>
                      <SelectItem value="sold">Sold</SelectItem>
                      <SelectItem value="unknown">Unknown</SelectItem>
                    </SelectContent>
                  </Select>
                </F>
                <F label="Reason (required)">
                  <Textarea
                    value={statusForm.reason}
                    onChange={(e) => setStatusForm({ ...statusForm, reason: e.target.value })}
                    rows={3}
                    placeholder="e.g. Sale cancelled — customer refund issued on 2026-07-06"
                    className="rounded-sm"
                    data-testid="status-change-reason"
                  />
                </F>
                <div className="text-[11px] text-[#C06C47] bg-[#C06C47]/10 border border-[#C06C47]/30 px-3 py-2">
                  This change is <b>audit-logged</b> with your name, timestamp and reason. If rolling back from Sold/Booked to Vacant, any active approved booking on this plot will be automatically marked rejected.
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setStatusChange(null)} className="rounded-sm">Cancel</Button>
                <Button onClick={submitStatusChange} data-testid="confirm-status-change" className="rounded-sm bg-[#C06C47] hover:bg-[#A65A38] text-white">
                  Apply change
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function Th({ children }) { return <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#5C6653] font-semibold whitespace-nowrap">{children}</th>; }
function Td({ children }) { return <td className="px-4 py-3 align-top">{children}</td>; }
function F({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-medium mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
